// >>> IT #146 — THE HOUSE'S ONE DOOR FROM THE ANGELS DRAWER TO THE SEAL SHE IS BEING SENT TO BUILD.
//
// HER TICKET (`daiwanalakia@gmail.com`), and HER RULING ON IT: *"YES, the angels carry over."* The
// control said *build your seal with these names* and carried none of them.
//
// **THE RETIRED CONTROL WAS A BARE `<a href={PAGE_PATHS.seals}>`**, which is a FULL PAGE
// NAVIGATION — so the app remounts (her drawer closing) and the drawer's `picks`, the only place
// those angels lived, go with it. One line, both halves of her report.
//
// >>> THIS IS `CauldronDoor`'s SHAPE AND NOT A SECOND MECHANISM (R28/R29). <<< That door exists for
// exactly this: a drawer control that opens a room AND asks the room to do something. It is an
// EVENT **and** a REQUEST left standing, because the navigation REMOUNTS the target page — a bare
// event fires before the listener exists, which is the defect TRAP-1a paid for by hand.
//
// >>> AND THE DROP IS A DEADLINE READ AT CONSUME TIME RATHER THAN A TIMER THAT FIRES. <<< That is
// TRAP-1a's own measured ruling and it avoids a real bug: with a `setTimeout`, **a SECOND tap inside
// the window leaves the FIRST tap's timer running, and that timer clears the NEWER request** — so
// she asks twice quickly and lands on the seal page with nothing in the box. It also makes the read
// IDEMPOTENT, which is what keeps it correct under StrictMode's double-invoked initialiser: the
// deadline is the only thing that drops it, so reading twice in one mount cannot lose her angels.
//
// The window is generous on purpose. `MINI_REQUEST_MS` was raised from two seconds to a band for a
// measured reason — a request can be dropped before the target page has finished mounting on a slow
// phone — and the cost of being generous here is only that a member who navigates BACK to the seal
// page within the window finds her angels again, which is what she asked for.
//
// **`win` IS A PARAMETER**, so a gate RUNS this against a fake window and reads back what it pushed,
// dispatched and flagged — including the DROP, which has no visible symptom (CauldronDoor's own
// recorded lesson: pure side effects on `window` made a live Chrome the only instrument that could
// reach it).
import { PAGE_PATHS } from "../lib/pages.js";
import { CLOSE_OVERLAYS_EVENT } from "./MobileNav.js";

export const OPEN_SEAL_NAMES_EVENT = "twelve:seal-names";

export const SEAL_PAGE = "seals";
export const sealPath = () => PAGE_PATHS[SEAL_PAGE];

// Long enough to survive a slow mount on a phone, short enough that yesterday's tap is not still
// waiting in a tab she left open.
export const SEAL_NAMES_REQUEST_MS = 15_000;

let handoff: { names: string[]; at: number } | null = null;

/**
 * WHAT THE SEAL CREATOR IS BEING HANDED, or nothing. Reading does NOT clear it — the deadline does
 * (above). An expired handoff is dropped here rather than returned, so a stale one can never fill a
 * form she opened an hour later.
 */
export function consumeSealNames(now: number = Date.now()): string[] {
  if (!handoff) return [];
  if (!Number.isFinite(now) || now - handoff.at > SEAL_NAMES_REQUEST_MS) { handoff = null; return []; }
  return handoff.names;
}

export function sealNamesPending(now: number = Date.now()): boolean {
  return consumeSealNames(now).length > 0;
}

/** Test seam only — the drop has no visible symptom, so a gate has to be able to set the floor. */
export function clearSealNames(): void { handoff = null; }

/**
 * TAKE HER THERE WITH HER ANGELS. An EMPTY list still navigates: the control only exists when she
 * has picks, and a door that silently refused to open because a name could not be read would be a
 * worse defect than the one this repairs (R19).
 */
export function goToSealCreator(
  names: string[],
  win: Window & typeof globalThis = window,
  now: number = Date.now(),
): void {
  handoff = { names: Array.isArray(names) ? names.filter((n) => typeof n === "string" && n.trim() !== "") : [], at: now };
  win.dispatchEvent(new CustomEvent(CLOSE_OVERLAYS_EVENT));
  const path = sealPath();
  if (win.location.pathname !== path) {
    win.history.pushState({ page: SEAL_PAGE }, "", path);
    win.dispatchEvent(new PopStateEvent("popstate"));
  }
  win.dispatchEvent(new CustomEvent(OPEN_SEAL_NAMES_EVENT));
  win.scrollTo({ top: 0, behavior: "smooth" });
}
